(ns practitest.frontend.forms3.requirements
  (:require
    [practitest.frontend.common-entities.metadata :as metadata]
    [practitest.frontend.common.i18n :refer [format-translation
                                             get-translation] :as i18n]
    [practitest.frontend.common.dom :as dom]
    [practitest.frontend.common-entities.events :as common-events]
    [practitest.frontend.common.mixpanel :as mixpanel]
    [practitest.frontend.common.permissions :as permissions]
    [practitest.frontend.common.re-frame :refer [partial-dispatch]]
    [practitest.frontend.components.icons :as icons]
    [practitest.frontend.components2.traceability :as traceability]
    [practitest.frontend.events :as events]
    [practitest.frontend.forms3.entity-form :as f3.entity-form]
    [practitest.frontend.forms3.entity-form.forms :as f3.forms]
    [practitest.frontend.forms3.entity-form.collab-ws :as collab-ws]
    [practitest.frontend.forms3.entity-form.utils :refer [track!]]
    [practitest.frontend.kanban :as kanban]
    [practitest.frontend.routing.backend-routes :as broutes]
    [practitest.frontend.subs :as subs]
    [practitest.frontend.common.bugtrackers :as bugtrackers]
    [practitest.frontend.issue.bugtracker :as bugtracker]
    [practitest.frontend.forms3.link-to-external-modal.main :as link-to-external]
    [practitest.frontend.forms3.link-to-external-modal.events :as link-to-external-events]
    [re-frame.core :as re-frame]))

(def current-entity :requirement)
(def metadata (get metadata/entities-metadata current-entity))
(def edit-permission (:edit-permissions metadata))
(def delete-permission (:delete-permissions metadata))
(def ^:private requirement-form-id (:form-id metadata))

(defn link-or-unlink-issue-to-external [requirement urls path-params]
  (let [requirement-external-id (:external_id requirement)
        current-project @(re-frame/subscribe [::subs/current-project])
        bugtracker-type (bugtracker/bugtracker-type current-project)
        jira? (bugtrackers/jira? bugtracker-type)
        clickup? (bugtrackers/clickup? bugtracker-type)
        sync-disabled? (and jira? (not (:requirements_sync_enabled current-project true)))
        bugtracker-name (bugtrackers/get-label bugtracker-type)
        unlink-from-jira-requirement-uri (broutes/get-url-view (:unlink_from_external urls) path-params)
        {:keys [edit]} (get-in metadata/entities-metadata [:requirement :urls])
        entity-url (broutes/get-url-view edit {:id (:id requirement)})]
    (when (or jira? clickup?)
      (if requirement-external-id
        {:text     (format-translation
                     (get-translation "entity.dropdown.unlink_form_bugtracker_issue" " Unlink from %{bt} Issue")
                     :bt bugtracker-name)
         :test-id  "requirement-form-menu-unlink-from-jira"
         :disabled? sync-disabled?
         :tooltip   (when sync-disabled?
                      (get-translation "entity.dropdown.sync_disabled_tooltip"
                                       "This option has been disabled by your administrator's settings."))
         :on-click (fn [e]
                     (dom/prevent-default e)
                     (when-not sync-disabled?
                       (track! (str "Unlink entity from" bugtracker-name) current-entity)
                       (re-frame/dispatch [::link-to-external-events/unlink-ticket
                                           {:uri unlink-from-jira-requirement-uri
                                            :params {:_method "post"
                                                     :id (:id requirement)
                                                     :p_id @(re-frame/subscribe [::subs/current-project-id])}}])
                       (set! (.-location js/window) entity-url)))
         :icon     [bugtracker/bugtracker-logo]}
        {:text     (format-translation
                     (get-translation "entity.dropdown.link_to_bugtracker_issue" " Link to %{bt} Issue")
                     :bt bugtracker-name)
         :test-id  "requirement-form-menu-link-to-jira"
         :disabled? sync-disabled?
         :tooltip   (when sync-disabled?
                      (get-translation "entity.dropdown.sync_disabled_tooltip"
                                       "This option has been disabled by your administrator's settings."))
         :on-click (fn [e]
                     (dom/prevent-default e)
                     (when-not sync-disabled?
                       (track! (str "Link entity to " bugtracker-name " issue") current-entity)
                       (link-to-external/show-link-to-external-modal)))
         :icon     [bugtracker/bugtracker-logo]}))))

(defn requirement-menu []
  (let [requirement (:current @(re-frame/subscribe [::subs/entity current-entity]))
        {:keys [urls]} metadata
        requirement-id (:id requirement)
        path-params {:id requirement-id}
        print-requirement-uri (broutes/get-url-view (:print urls) path-params {"ids" requirement-id})
        delete-permission? (permissions/has-permission? delete-permission)]
    [(link-or-unlink-issue-to-external requirement urls path-params)
     {:text     (get-translation "generic.button.delete" "Delete")
      :test-id  "requirement-form-menu-delete"
      :disabled? (not delete-permission?)
      :on-click (fn [e]
                  (when delete-permission?
                    (dom/prevent-default e)
                    (when (js/confirm (get-translation "entity.confirm.action-delete" "Are you sure? This Operation cannot be undone."))
                      (track! "Delete entity" current-entity)
                      (re-frame/dispatch [::common-events/delete-current-entity requirement-form-id]))))
      :icon     [icons/mux-trash]}
     {:text        (get-translation "generic.button.print" "Print")
      :test-id     "requirement-form-menu-print"
      :icon        [icons/mux-printer]
      :new-window? true
      :mixpanel    ["Form Actions" {"Entity" "Requirement" "Action" "Print entity"}]
      :href        print-requirement-uri}]))

(defn build-requirement-tabs
  [edit?]
  [(f3.entity-form/general-entity-tab)

   (traceability/build-traceability-tab)

   (when edit?
     (f3.entity-form/history-tab))])

(defn- bugtracker-fields [base-entity all-fields]
  (let [linked-requirement? (boolean (:external_id base-entity))]
    (if linked-requirement?
      (filter
        #(true? (get-in % [:bugtracker :requirement]))
        (bugtracker/bugtracker-fields all-fields))
      [])))

(defn- requirement-form-layout
  [entity-fields external-id]
  (let [kanban-enabled? @(re-frame/subscribe [::subs/kanban-enabled?])
        nil-or-unavailable? (fn [field]
                              (or (nil? field)
                                  (not (contains? entity-fields field))))
        initial-layout {:title {:fields [{:field :name}]}
                        (:description f3.entity-form/sections-keys) {:fields [{:field :description}]}
                        (:testing-status f3.entity-form/sections-keys) {}}

        existing-fields (set (f3.forms/flatten-layout initial-layout))
        to-remove? (fn [field]
                     (or (nil-or-unavailable? field)
                         (contains? existing-fields field)))

        filtered-entity-fields (cond-> (f3.entity-form/filter-entity-fields entity-fields to-remove?)
                                       kanban-enabled? (assoc :kanban_lane kanban/kanban-field))
        filtered-entity-fields-flat (vec (keys filtered-entity-fields))
        entity-sections @(re-frame/subscribe [::subs/entity-sections current-entity])

        update-main-sections (if (some? entity-sections)
                               #(f3.entity-form/update-layout % entity-sections filtered-entity-fields)
                               #(f3.entity-form/update-layout % (:general-info f3.entity-form/sections-keys) filtered-entity-fields-flat "general_info" entity-fields))

        layout (-> initial-layout
                   (update-main-sections)
                   (f3.entity-form/handle-integration-fields external-id))]
    layout))

(def new-controller
  {:start (fn [params]
            (re-frame/dispatch [::f3.forms/reset-form-state requirement-form-id true current-entity])
            (re-frame/dispatch [::events/set-current-entity-type current-entity])
            (re-frame/dispatch [::events/set-new-entity-traceability current-entity])

            (let [project-id (get-in params [:path :project-id])]
              (re-frame/dispatch [::f3.entity-form/fetch-fields {:form-id requirement-form-id
                                                                 :entity-type current-entity
                                                                 :project-id project-id
                                                                 :new? true}])))
   :identity :parameters})

(def edit-controller
  {:start    (fn [params]
               (let [{:keys [requirement-id project-id]} (:path params)]
                 (re-frame/dispatch [::f3.forms/reset-form-state requirement-form-id false current-entity])
                 (re-frame/dispatch [::events/set-current-entity-type current-entity])
                 (re-frame/dispatch [::subs/set-current-entity-id
                                     current-entity
                                     requirement-id])

                 (let [fetch-options {:form-id requirement-form-id
                                      :entity-type current-entity
                                      :entity-id requirement-id
                                      :project-id project-id
                                      :new? false}]
                   (re-frame/dispatch [::f3.entity-form/fetch-fields fetch-options])
                   (re-frame/dispatch [::f3.entity-form/fetch-entity fetch-options]))
                 (re-frame/dispatch [::collab-ws/subscribe-to-form-ws [(:plural-key metadata)]])
                 (re-frame/dispatch [::events/fetch-comments])
                 (re-frame/dispatch [::events/fetch-attachments])
                 (re-frame/dispatch [::events/fetch-history])
                 (re-frame/dispatch [::common-events/trigger-online-request])
                 (re-frame/dispatch [::common-events/reset-expanded current-entity (:requirement subs/index-grid-keys)])
                 (mixpanel/track "Grid Actions" {"Action" "Edit Requirement"})))
   :stop     (fn [_params]
               (re-frame/dispatch [::events/clear-current-entity current-entity])
               (re-frame/dispatch [::collab-ws/unsubscribe-form-ws [(:plural-key metadata)]])
               (re-frame/dispatch [::common-events/reset-expanded :requirement :history2])
               (re-frame/dispatch [::f3.forms/reset-form-state requirement-form-id false current-entity]))
   :identity :parameters})

(defmethod f3.forms/build-entity-form-layout requirement-form-id
  [_ _ _ entity-fields entity]
  (requirement-form-layout entity-fields (:external_id entity)))

(defn main [new?]
  (let [current-requirement (:current @(re-frame/subscribe [::subs/entity current-entity]))
        external-id (:external_id current-requirement)]
    [:<>
     [bugtracker/bugtracker-integration-modal]
     [f3.entity-form/entity-form
      {:new?              new?
       :popup-menu        (requirement-menu)
       :on-save           (partial-dispatch [::common-events/save-entity requirement-form-id :requirement])
       :entity-type       current-entity
       :form-id           requirement-form-id
       :breadcrumbs       [{:href  (broutes/get-url-view (get-in metadata [:urls :index]) {:project-id (:id @(re-frame/subscribe [::subs/current-project]))})
                            :label (get-translation "navigation.project-menu.requirements" "Requirements")}]
       :header-tabs       (build-requirement-tabs (not new?))
       :edit-permissions? (permissions/has-permission? edit-permission)
       :customization-permissions? (permissions/has-permission? "customization")
       :additional-fields (when external-id
                            bugtracker/additional-fields-form3)
       :general-form-inner-tabs {:new #{:attachments}
                                 :edit #{:attachments :comments}}
       :default-active-sub-tab :attachments}]]))
